import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
const colourImageCache = new Map();

function warmColourImage(option, sizes) {
  const key = `${option.dataset.klImageSrc}|${sizes}`;
  if (colourImageCache.has(key)) return colourImageCache.get(key);
  const promise = new Promise((resolve) => {
    const image = new Image();
    image.onload = image.onerror = () => resolve();
    image.decoding = 'async';
    image.fetchPriority = 'low';
    image.sizes = sizes;
    image.srcset = option.dataset.klImageSrcset || '';
    image.src = option.dataset.klImageSrc;
  });
  colourImageCache.set(key, promise);
  if (colourImageCache.size > 32) colourImageCache.delete(colourImageCache.keys().next().value);
  return promise;
}

export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    const schedule = () => {
      const run = () => this.#warmColours();
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 5000 });
      else window.setTimeout(run, 1200);
    };
    if (document.readyState === 'complete') schedule();
    else window.addEventListener('load', schedule, { once: true, signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();
  #selectionGeneration = 0;
  #previewRestore;

  #firstImages() {
    return Array.from(this.querySelectorAll(
      'slideshow-slide:first-child .product-media__image, .media-gallery__grid > li:first-child .product-media__image'
    ));
  }

  async #warmColours() {
    if (!this.isConnected || navigator.connection?.saveData || /(^|-)2g$/.test(navigator.connection?.effectiveType || '')) return;
    const picker = this.closest('.shopify-section')?.querySelector('variant-picker[data-template-product-match="true"]');
    if (!picker || picker.closest('product-card, quick-add-dialog')) return;
    const sizes = this.#firstImages()[0]?.sizes || '100vw';
    const options = Array.from(picker.querySelectorAll('input[data-kl-image-src]'));
    let index = 0;
    const worker = async () => {
      while (this.isConnected && index < options.length) await warmColourImage(options[index++], sizes);
    };
    await Promise.all([worker(), worker()]);
  }

  #previewColour(picker) {
    if (picker.dataset.templateProductMatch !== 'true' || picker.closest('product-card, quick-add-dialog')) return;
    const option = picker.querySelector('input[data-kl-image-src]:checked');
    if (!option || option.dataset.connectedProductUrl) return;
    const images = this.#firstImages();
    if (!images.length) return;
    if (!this.#previewRestore && images.every((image) => image.closest('.product-media')?.dataset.mediaId === option.dataset.klImageId)) return;
    if (!this.#previewRestore) {
      const snapshots = images.map((image) => ({ image, attributes: Array.from(image.attributes, (a) => [a.name, a.value]) }));
      const wasInert = this.inert;
      this.#previewRestore = () => {
        for (const { image, attributes } of snapshots) {
          for (const attribute of Array.from(image.attributes)) image.removeAttribute(attribute.name);
          for (const [name, value] of attributes) image.setAttribute(name, value);
        }
        this.inert = wasInert;
      };
    }
    this.inert = true;
    for (const image of images) {
      image.alt = option.dataset.klImageAlt || '';
      image.width = Number(option.dataset.klImageWidth);
      image.height = Number(option.dataset.klImageHeight);
      image.loading = 'eager';
      image.srcset = option.dataset.klImageSrcset || '';
      image.src = option.dataset.klImageSrc;
    }
    const label = option.closest('fieldset')?.querySelector('legend .variant-option__swatch-value');
    if (label) label.textContent = option.value;
    this.slideshow?.select(0, undefined, { animate: false });
  }

  #restorePreview() {
    this.#previewRestore?.();
    this.#previewRestore = undefined;
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const generation = ++this.#selectionGeneration;
    this.#previewColour(event.target);
    event.promise
      .then(({ detail }) => {
        if (generation !== this.#selectionGeneration) return;
        if (!detail?.html) { this.#restorePreview(); return; }

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) { this.#restorePreview(); return; }

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (generation === this.#selectionGeneration) this.#restorePreview();
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}

